import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { View } from "react-native"

import { Dialog as ThemedDialog } from "@rn-vui/themed"
import { useQueryClient } from "@tanstack/react-query"
import { UserRole } from "types/role"

import { Button, Text } from "src/components/Ignite"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCloseCurrentConversation } from "src/queries/useConversation"
import {
  clearUserSelectedSession,
  getUserSelectedSession,
  setUserSelectedSession,
  useCurrentRole,
  useResetRole,
  useRoleInfo,
  useRoleInfoQuery,
  UserSelectedSession,
  useSwitchRole,
} from "src/queries/useRole"
import { logSentryBreadcrumb, logSentryEvent } from "src/utils/crashReporting"
import { useAuth } from "./AuthContext"
import { useWebsocket } from "./WebsocketContext"

const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  sponsor_admin: "Program Manager",
  sme: "Expert",
  member: "End User",
  testing_member: "End User - Test",
}

interface RoleContextType {
  switchRole: (targetRole: UserRole) => Promise<void>
  resetRole: () => Promise<void>
  resetUnityAvatars: () => void
  verifyAndRestoreSession: (skipReconnect?: boolean) => Promise<boolean>
  currentRole: UserRole | undefined
  roleInfo: ReturnType<typeof useRoleInfo>
  isSwitchingRole: boolean
}

const RoleContext = createContext<RoleContextType | null>(null)

export const RoleProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { theme } = useAppTheme()
  const { logout, api, sponsorId } = useAuth()
  const queryClient = useQueryClient()
  const { isConnected, getWebSocket } = useWebsocket()
  const closeConversation = useCloseCurrentConversation()
  const switchRoleMutation = useSwitchRole()
  const resetRoleMutation = useResetRole()
  const roleInfo = useRoleInfo()
  const roleInfoQuery = useRoleInfoQuery()
  const currentRole = useCurrentRole()

  // Track if we're currently restoring a session to prevent infinite loops
  const [isRestoringSession, setIsRestoringSession] = useState(false)
  const sessionRestorationAttemptRef = useRef(false)

  // Session expired dialog state
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false)
  const [expiredRoleName, setExpiredRoleName] = useState<string>("")

  const reconnectWebSocket = useCallback(() => {
    console.log("[ROLE_CONTEXT] Reconnecting WebSocket for new role")
    // WebSocket will automatically reconnect with new user context
    // when the connection URL changes due to auth token refresh
    if (isConnected) {
      const webSocket = getWebSocket()
      if (webSocket) {
        // Close current connection to force reconnect with new user_id
        webSocket.close()
      }
    }
  }, [isConnected, getWebSocket])

  /**
   * Attempts to restore the user's intended session (sponsor and role) when drift is detected.
   * Called when the server-side session has reverted to defaults.
   */
  const attemptSessionRestoration = useCallback(
    async (userSelectedSession: UserSelectedSession, skipReconnect?: boolean): Promise<boolean> => {
      setIsRestoringSession(true)
      sessionRestorationAttemptRef.current = true

      const { roleName: lastRole, sponsorId: targetSponsorId } = userSelectedSession

      try {
        console.log(
          `[ROLE_CONTEXT] attemptSessionRestoration: Restoring to sponsor="${targetSponsorId}" and role="${lastRole}"`,
        )

        // First close any open conversation (continue even if this fails)
        await closeConversation.mutateAsync().catch((error) => {
          console.warn("[ROLE_CONTEXT] Failed to close conversation during restoration", error)
        })

        // 1. Restore the sponsor context if it differs from what we expect.
        // Note: we don't use AuthContext.switchSponsor() here because that clears all data;
        // we just need the API to target the correct sponsor for the subsequent role switch.
        // Hold all concurrent requests until the context switch completes so that
        // no racing response can overwrite it with a stale context token.
        api.beginSessionSwitch()
        try {
          const sponsorResponse = await api.switchContext({
            sponsorId: targetSponsorId,
            isLogin: false,
          })
          if (sponsorResponse.kind !== "ok") {
            throw new Error(`Failed to restore sponsor: ${sponsorResponse.kind}`)
          }
        } finally {
          api.completeSessionSwitch()
        }

        // 2. Switch to the last selected role
        await switchRoleMutation.mutateAsync(lastRole)

        console.log(`[ROLE_CONTEXT] Successfully restored session to "${lastRole}"`)
        logSentryBreadcrumb(`session_restoration_success:${lastRole}`)

        // Reconnect WebSocket with restored context
        if (!skipReconnect) {
          reconnectWebSocket()
        }
        return true
      } catch (error) {
        console.error(`[ROLE_CONTEXT] Failed to restore session:`, error)
        logSentryEvent("error", "session_drift_restoration_failed", {
          error: error instanceof Error ? error.message : String(error),
          targetRole: lastRole,
          targetSponsor: targetSponsorId,
        })
        // Show session expired dialog instead of leaving user in inconsistent state
        setExpiredRoleName(ROLE_DISPLAY_NAMES[lastRole] || lastRole)
        setShowSessionExpiredDialog(true)
        return false
      } finally {
        setIsRestoringSession(false)
        sessionRestorationAttemptRef.current = false
      }
    },
    [api, closeConversation, switchRoleMutation, reconnectWebSocket],
  )

  /**
   * Session Reconciliation Effect
   *
   * Detects when the server-side session has reverted to defaults using the
   * user_id fingerprint. Automatically triggers restoration if mismatch is found.
   */
  useEffect(() => {
    // Don't attempt restoration if we're already in the middle of switching roles
    if (isRestoringSession || switchRoleMutation.isPending) {
      return
    }

    // Wait until we have valid role information from the server
    if (!roleInfo?.current_user_id) {
      return
    }

    const userSelectedSession = getUserSelectedSession()

    // If we have an intended state, check for drift
    if (userSelectedSession) {
      if (roleInfo.current_user_id !== userSelectedSession.userId) {
        // Prevent multiple restoration attempts for the same mismatch
        if (sessionRestorationAttemptRef.current) {
          return
        }

        console.log(
          `[ROLE_CONTEXT] Session drift detected (via fingerprint): server="${roleInfo.current_user_id}", intended="${userSelectedSession.userId}". Restoring...`,
        )
        logSentryEvent("warning", "session_drift_detected", {
          serverUserId: roleInfo.current_user_id,
          intendedUserId: userSelectedSession.userId,
          intendedRole: userSelectedSession.roleName,
          intendedSponsor: userSelectedSession.sponsorId,
        })

        attemptSessionRestoration(userSelectedSession)
      } else {
        // Reset the attempt flag when there's no mismatch
        sessionRestorationAttemptRef.current = false
      }
    }
  }, [roleInfo, isRestoringSession, switchRoleMutation.isPending, attemptSessionRestoration])

  const resetUnityAvatars = useCallback(() => {
    console.log("[ROLE_CONTEXT] Resetting Unity avatars after role switch")

    // Wait a short time for MSK data to be refetched, then reset Unity
    setTimeout(() => {
      try {
        // Force Unity to reinitialize by triggering a re-render
        // This will cause Unity context to re-evaluate with new MSK data
        queryClient.invalidateQueries({ queryKey: ["msk"] })
        console.log("[ROLE_CONTEXT] Unity avatar reset initiated")
      } catch (error) {
        console.warn("[ROLE_CONTEXT] Failed to reset Unity avatars:", error)
      }
    }, 1000) // Give time for MSK data to load
  }, [queryClient])

  const switchRole = useCallback(
    async (targetRole: UserRole) => {
      try {
        console.log("[ROLE_CONTEXT] Switching to role:", targetRole)

        // FIXME: it's unclear why closing the conversation before swiching roles fixes
        // subtle problems with role swiching. It would be good to confirm with the backend
        // again if this is the expected sequence or if this might represent a timing bug.
        try {
          await closeConversation.mutateAsync()
          console.log("useSwitchRoleTestUser: closed conversation")
        } catch (error) {
          console.error(
            "useSwitchRoleTestUser: Failed to close conversation before deleting user history",
            error,
          )
        }

        // Call the API to switch roles
        await switchRoleMutation.mutateAsync(targetRole)

        // Reset Unity avatars after data is cleared
        resetUnityAvatars()

        // Reconnect WebSocket with new user context
        reconnectWebSocket()

        api.reportActivity(Date.now(), "roleChange", "roleChange").catch((error) => {
          console.warn("[ROLE_CONTEXT] Failed to report roleChange activity:", error)
        })

        console.log("[ROLE_CONTEXT] Successfully switched to role:", targetRole)
      } catch (error) {
        console.error("[ROLE_CONTEXT] Failed to switch role:", error)
        throw error
      }
    },
    [resetUnityAvatars, reconnectWebSocket, api, closeConversation, switchRoleMutation],
  )

  const resetRole = useCallback(async () => {
    try {
      console.log("[ROLE_CONTEXT] Resetting to main role")

      // Call the API to reset role
      await resetRoleMutation.mutateAsync()

      // Reset Unity avatars after data is cleared
      resetUnityAvatars()

      // Reconnect WebSocket with new user context
      reconnectWebSocket()

      console.log("[ROLE_CONTEXT] Successfully reset to main role")
    } catch (error) {
      console.error("[ROLE_CONTEXT] Failed to reset role:", error)
      throw error
    }
  }, [resetRoleMutation, resetUnityAvatars, reconnectWebSocket])

  // Session expired dialog handlers
  const handleSwitchToDefaultRole = useCallback(async () => {
    setShowSessionExpiredDialog(false)
    clearUserSelectedSession()
    // Reset to the default role
    try {
      await resetRole()
    } catch (error) {
      console.error("[ROLE_CONTEXT] Failed to switch to default role:", error)
      // If switching fails, show the dialog again
      setShowSessionExpiredDialog(true)
    }
  }, [resetRole])

  const handleLogoutClick = useCallback(async () => {
    setShowSessionExpiredDialog(false)
    clearUserSelectedSession()
    await logout()
  }, [logout])

  const isSwitchingRole =
    switchRoleMutation.isPending || resetRoleMutation.isPending || isRestoringSession

  /**
   * Session Fingerprint Pinning Effect
   *
   * After a stable state is reached (e.g., after login or sponsor switch),
   * if we don't have a userSelectedSession yet, "pin" the current server state
   * to establish the baseline for future drift detection.
   */
  useEffect(() => {
    if (isSwitchingRole || !roleInfo?.current_user_id) return

    const userSelectedSession = getUserSelectedSession()
    if (!userSelectedSession) {
      console.log("[ROLE_CONTEXT] Pinning session fingerprint:", roleInfo.current_user_id)
      setUserSelectedSession({
        sponsorId,
        roleName: roleInfo.current_role,
        userId: roleInfo.current_user_id,
      })
    }
  }, [roleInfo, isSwitchingRole, sponsorId])

  /**
   * Verifies the current session state with the server and restores if drift is detected.
   * Returns true if the session was restored, false if no restoration was needed.
   * Uses current_user_id as a fingerprint for the entire session (Sponsor + Role).
   */
  const verifyAndRestoreSession = useCallback(
    async (skipReconnect?: boolean): Promise<boolean> => {
      console.log("[ROLE_CONTEXT] verifyAndRestoreSession: Checking session fingerprint...")

      try {
        // Refetch role info from server to get fresh state
        const { data: freshRoleInfo } = await roleInfoQuery.refetch()

        if (!freshRoleInfo) {
          console.warn("[ROLE_CONTEXT] verifyAndRestoreSession: Could not fetch role info")
          return false
        }

        const userSelectedSession = getUserSelectedSession()

        // If we don't have an intended state, we can't detect drift
        if (!userSelectedSession) {
          console.log(
            "[ROLE_CONTEXT] verifyAndRestoreSession: No intended state to compare against",
          )
          return false
        }

        // Check for drift: if current_user_id doesn't match our fingerprint
        if (freshRoleInfo.current_user_id !== userSelectedSession.userId) {
          console.log(
            `[ROLE_CONTEXT] verifyAndRestoreSession: Drift detected! Server=" ${freshRoleInfo.current_user_id}", Intended="${userSelectedSession.userId}". Restoring...`,
          )
          logSentryEvent("warning", "session_drift_detected_manual", {
            serverUserId: freshRoleInfo.current_user_id,
            intendedUserId: userSelectedSession.userId,
          })

          return await attemptSessionRestoration(userSelectedSession, skipReconnect)
        }

        console.log(`[ROLE_CONTEXT] verifyAndRestoreSession: Session fingerprint matches`)
        return false
      } catch (error) {
        console.error(
          "[ROLE_CONTEXT] verifyAndRestoreSession: Error checking/restoring session:",
          error,
        )
        return false
      }
    },
    [roleInfoQuery, attemptSessionRestoration],
  )

  const value: RoleContextType = {
    switchRole,
    resetRole,
    resetUnityAvatars,
    verifyAndRestoreSession,
    currentRole,
    roleInfo,
    isSwitchingRole,
  }

  return (
    <RoleContext.Provider value={value}>
      {children}

      {/* Session Expired Dialog */}
      <ThemedDialog
        isVisible={showSessionExpiredDialog}
        onBackdropPress={() => {}} // Prevent dismissing by tapping backdrop
        overlayStyle={{
          backgroundColor: theme.colors.background,
          borderRadius: 12,
          padding: 24,
          maxWidth: 360,
          width: "90%",
        }}
      >
        <ThemedDialog.Title
          title="Session Expired"
          titleStyle={{
            color: theme.colors.text,
            fontWeight: "600",
            fontSize: 20,
            marginBottom: 12,
          }}
        />
        <Text style={{ marginBottom: 24 }}>
          Your session for the {expiredRoleName} role has expired. You can switch back to your
          default role or logout.
        </Text>
        <View style={{ gap: 12 }}>
          <Button
            text="Switch to Default Role"
            preset="subtle"
            onPress={handleSwitchToDefaultRole}
          />
          <Button text="Logout" preset="subtle" onPress={handleLogoutClick} />
        </View>
      </ThemedDialog>
    </RoleContext.Provider>
  )
}

export const useRole = () => {
  const context = useContext(RoleContext)
  if (!context) {
    throw new Error("useRole must be used within a RoleProvider")
  }
  return context
}
